/**
 * Debug Logger - JavaScript version
 * This is a compatibility layer to support both TypeScript and JavaScript imports
 */

/**
 * Get debug state from localStorage
 */
function getDebugState() {
  try {
    // Check if we're in browser environment
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return {
        debugEnabled: false,
        debugAuth: false,
        debugAPI: false,
        debugBlog: false,
        debugDealer: false,
        debugCache: false,
        debugPricing: false,
        debugAdmin: false,
        debugFavorites: false,
        debugCoin: false,
        debugAudio: false,
        debugSSR: false,
      };
    }

    const stored = localStorage.getItem('gildy-store');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        debugEnabled: parsed.state?.debugEnabled || false,
        debugAuth: parsed.state?.debugAuth || false,
        debugAPI: parsed.state?.debugAPI || false,
        debugBlog: parsed.state?.debugBlog || false,
        debugDealer: parsed.state?.debugDealer || false,
        debugCache: parsed.state?.debugCache || false,
        debugPricing: parsed.state?.debugPricing || false,
        debugAdmin: parsed.state?.debugAdmin || false,
        debugFavorites: parsed.state?.debugFavorites || false,
        debugCoin: parsed.state?.debugCoin || false,
        debugAudio: parsed.state?.debugAudio || false,
        debugNotifications: parsed.state?.debugNotifications || false,
      };
    }
  } catch (error) {
    // Fallback to disabled state
  }
  
  return {
    debugEnabled: false,
    debugAuth: false,
    debugAPI: false,
    debugBlog: false,
    debugDealer: false,
    debugCache: false,
    debugPricing: false,
    debugAdmin: false,
    debugFavorites: false,
    debugCoin: false,
    debugAudio: false,
    debugSSR: false,
    debugNotifications: false,
  };
}

/**
 * Check if a debug category is enabled
 */
function isDebugEnabled(category) {
  const state = getDebugState();
  
  // If general debug is disabled, return false
  if (!state.debugEnabled) {
    return false;
  }
  
  // Check specific category
  switch (category) {
    case 'auth':
      return state.debugAuth;
    case 'api':
      return state.debugAPI;
    case 'blog':
      return state.debugBlog;
    case 'dealer':
      return state.debugDealer;
    case 'cache':
      return state.debugCache;
    case 'pricing':
      return state.debugPricing;
    case 'admin':
      return state.debugAdmin;
    case 'favorites':
      return state.debugFavorites;
    case 'coin':
      return state.debugCoin;
    case 'audio':
      return state.debugAudio;
    case 'notifications':
      return state.debugNotifications;
    case 'ssr':
      return state.debugSSR;
    case 'general':
      return true; // General is enabled if debugEnabled is true
    default:
      return false;
  }
}

/**
 * Debug logger that respects debug settings
 * Only logs when the specific category is enabled
 */
export function debugLog(category, message, data, source) {
  if (!isDebugEnabled(category)) {
    return;
  }
  
  const prefix = `[DEBUG:${category.toUpperCase()}]${source ? ` ${source}:` : ''}`;
  
  if (data !== undefined) {
    console.log(prefix, message, data);
  } else {
    console.log(prefix, message);
  }
}

/**
 * Debug error logger (always enabled for errors, but respects category for additional info)
 */
export function debugError(category, message, error, source) {
  const prefix = `[ERROR:${category.toUpperCase()}]${source ? ` ${source}:` : ''}`;
  
  // Always log errors
  if (error !== undefined) {
    console.error(prefix, message, error);
  } else {
    console.error(prefix, message);
  }
  
  // Additional debug info only if category is enabled
  if (isDebugEnabled(category) && error) {
    console.log(`[DEBUG:${category.toUpperCase()}] Additional error details:`, {
      stack: error.stack,
      cause: error.cause,
      timestamp: new Date().toISOString(),
    });
  }
}

/**
 * Warning logger that respects debug settings
 */
export function debugWarn(category, message, data, source) {
  if (!isDebugEnabled(category)) {
    return;
  }
  
  const prefix = `[WARN:${category.toUpperCase()}]${source ? ` ${source}:` : ''}`;
  
  if (data !== undefined) {
    console.warn(prefix, message, data);
  } else {
    console.warn(prefix, message);
  }
}